import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Trophy, ArrowLeft, Calendar, Users, Coins } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Tournaments({ agent }) {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      const response = await axios.get(`${API}/tournaments`);
      setTournaments(response.data);
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="font-unbounded text-2xl font-bold text-gradient">TOURNAMENTS</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <Trophy size={48} className="mx-auto mb-4 text-[#FFD700]" />
          <h2 className="font-unbounded text-3xl md:text-4xl font-bold mb-2">Competitive Arenas</h2>
          <p className="font-inter text-slate-400">Join tournaments and compete for $MANA prizes</p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
          {loading ? (
            <div className="col-span-2 text-center py-12">
              <p className="font-rajdhani text-xl text-slate-400">Loading tournaments...</p>
            </div>
          ) : tournaments.length === 0 ? (
            <div className="col-span-2 text-center py-12">
              <p className="font-rajdhani text-xl text-slate-400">No active tournaments</p>
            </div>
          ) : (
            tournaments.map((tournament, index) => (
              <motion.div
                key={tournament.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  data-testid={`tournament-${tournament.id}`}
                  className="glass-card border-white/10 hover:border-[#7000FF]/50 transition-all duration-300 hover:scale-[1.02]"
                >
                  <CardHeader>
                    <CardTitle className="font-unbounded text-2xl text-white flex items-center gap-3">
                      <Trophy className="text-[#FFD700]" size={28} />
                      {tournament.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <InfoItem
                        icon={<Users size={18} className="text-[#00F0FF]" />}
                        label="Format"
                        value={tournament.format}
                      />
                      <InfoItem
                        icon={<Users size={18} className="text-[#7000FF]" />}
                        label="Participants"
                        value={`${tournament.participants} max`}
                      />
                      <InfoItem
                        icon={<Coins size={18} className="text-[#FFD700]" />}
                        label="Entry Fee"
                        value={`${tournament.entry_fee} $MANA`}
                      />
                      <InfoItem
                        icon={<Trophy size={18} className="text-[#00FF9D]" />}
                        label="Prize Pool"
                        value={`${tournament.prize_pool.toLocaleString()} $MANA`}
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-4 border-t border-white/10">
                      <Calendar size={16} className="text-slate-400" />
                      <span className="font-rajdhani text-sm text-slate-400">
                        Starts: {formatDate(tournament.starts_at)}
                      </span>
                    </div>

                    <Button
                      data-testid={`join-${tournament.id}-btn`}
                      className="w-full bg-[#7000FF] hover:bg-[#5000CC] text-white font-rajdhani py-6 rounded-full shadow-[0_0_15px_rgba(112,0,255,0.3)] transition-all"
                    >
                      Join Tournament
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))
          )}
        </div>

        {/* Coming Soon Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <Card className="glass-card border-white/10 max-w-2xl mx-auto">
            <CardContent className="p-8">
              <h3 className="font-unbounded text-2xl font-bold mb-3 text-gradient">Global Championship</h3>
              <p className="font-inter text-slate-400 mb-4">
                Monthly championship with 2,048+ participants. Prize pool starts at 1.5M $MANA!
              </p>
              <span className="inline-block font-rajdhani text-sm px-4 py-2 bg-[#00F0FF]/20 border border-[#00F0FF]/50 rounded-full text-[#00F0FF]">
                Coming Soon
              </span>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value }) {
  return (
    <div className="bg-white/5 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="font-rajdhani text-xs text-slate-500 uppercase">{label}</span>
      </div>
      <p className="font-rajdhani text-lg font-bold text-white">{value}</p>
    </div>
  );
}